import React from 'react';
import { CmhcIcon, Spinner, SpinnerSize, SpinnerVariant, CmhcFlexIcon } from '@cmhc-schl/hearth-ui';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import styles from './styles.module.scss';

const serviceNowLink = (
  <a
    className="piAnchorTagExternalLinkIcon"
    href="https://cmhc.service-now.com/cmhc"
    target="_blank"
    rel="noopener noreferrer"
  >
    <u>ServiceNow</u>
  </a>
);

export const SpinnerElement = () => (
  <Box className={styles.spinnerWrapper}>
    <Spinner
      data-testid="requestOverviewSpinner"
      id="request-overview-spinner"
      size={SpinnerSize.LARGE}
      type={SpinnerVariant.PRIMARY}
      ariaText="Loading Data"
      modifiers={{ size: 20, thickness: 2 }}
    />
  </Box>
);

type ErrorMessageProps = {
  errorType: string;
  header?: string;
  content?: string;
};

const ErrorMessage: React.FC<ErrorMessageProps> = ({ errorType, header, content }) => {
  const { t } = useTranslation('HbtPortfolioManagementRequestOverview');
  const errorConfig: Record<string, { icon: string; header: string; content: string }> = {
    api_error: {
      icon: 'icon_error',
      header: t('Pi-Portfolio-Management-API-Error-Header'),
      content: t('Pi-Portfolio-Management-API-Error-Content')
    },
    no_data_error: {
      icon: 'icon_circle_x',
      header: t('Pi-Portfolio-Management-Empty-Data-Error-Header'),
      content: t('Pi-Portfolio-Management-Empty-Data-Error-Content')
    },
    no_data_error_master_pool: {
      icon: 'icon_circle_x',
      header: t('Pi-Portfolio-Management-Empty-Data-Error-Header'),
      content: t('Pi-Portfolio-Management-Empty-Data-Error-Content_Master_Pool')
    }
  };

  const {
    icon,
    header: defaultHeader,
    content: defaultContent
  } = errorConfig[errorType] || errorConfig.api_error;

  const resolvedHeader = header ?? defaultHeader;
  const resolvedContent = content ?? defaultContent;
  const hasServiceNowToken = resolvedContent.includes('{{ServiceNow}}');
  const [before = '', after = ''] = hasServiceNowToken
    ? resolvedContent.split('{{ServiceNow}}')
    : [resolvedContent, ''];

  return (
    <Box
      className={`${styles.portfolioErrorContainer} ${icon === 'icon_error' ? styles.errorIcon : ''}`}
    >
      {icon === 'icon_circle_x' ? (
        <CmhcFlexIcon type={icon} width={48} height={48} />
      ) : (
        <CmhcIcon type={icon} />
      )}
      <Box className={styles.errorMessageContainer}>
        <p>
          <span>{resolvedHeader}</span>
        </p>
        <p>
          {hasServiceNowToken ? (
            <>
              {before}
              {serviceNowLink}
              {after}
            </>
          ) : (
            resolvedContent
          )}
        </p>
      </Box>
    </Box>
  );
};

export default ErrorMessage;
